import { Button } from '@latitude/button';
import { cn } from '@latitude/token-utils';
import type { Ref } from 'react';

import type { ButtonStackButtonProps, ButtonStackProps } from './types';

/**
 * A smaller helper for handing each button to its optional `wrapper`
 */
const ButtonStackButton = ({
  wrapper,
  ...buttonProps
}: ButtonStackButtonProps) => {
  const button = <Button {...buttonProps} />;
  return wrapper ? wrapper(button) : button;
};

/**
 * A component that displays up to three action buttons in a horizontal layout.
 *
 * @component
 * @example
 * // Basic usage with all three buttons. The defaults give ghost, outline and
 * // filled left to right, so hierarchy reads without being restated here and
 * // no two filled buttons ever sit side by side.
 * <ButtonStack
 *   tertiaryButton={{ children: 'Cancel', onClick: handleCancel }}
 *   secondaryButton={{ children: 'Save Draft', onClick: handleSaveDraft }}
 *   primaryButton={{ children: 'Publish', onClick: handlePublish }}
 * />
 *
 * @example
 * // Custom variants and types:
 * <ButtonStack
 *   secondaryButton={{
 *     children: 'Delete',
 *     variant: 'danger',
 *     type: 'filled'
 *   }}
 *   primaryButton={{ children: 'Confirm' }}
 * />
 *
 * @param {Object} props - The component props.
 * @param {ButtonProps} [props.tertiaryButton] - Props for the tertiary button (leftmost).
 * @param {ButtonProps} [props.secondaryButton] - Props for the secondary button (middle).
 * @param {ButtonProps} [props.primaryButton] - Props for the primary button (rightmost).
 * @param {string} [props.className] - Additional CSS class name(s) for the container.
 * @param {string} [props.data-testid='button-stack'] - Test ID for the component.
 * @returns {React.ReactElement} The rendered ButtonStack component.
 */
export const ButtonStack = ({
  tertiaryButton,
  secondaryButton,
  primaryButton,
  className,
  'data-testid': testID = 'button-stack',
  ref,
}: ButtonStackProps & {
  ref?: Ref<HTMLDivElement>;
}) => {
  // If no buttons are provided, return null
  if (!tertiaryButton && !secondaryButton && !primaryButton) {
    return null;
  }

  return (
    <div
      ref={ref}
      data-testid={testID}
      className={cn('flex items-center gap-3', className)}
    >
      {tertiaryButton && (
        <ButtonStackButton
          variant="neutral"
          type="ghost"
          size="small"
          {...tertiaryButton}
          data-testid={
            tertiaryButton['data-testid'] || `${testID}-tertiary-button`
          }
        />
      )}
      {secondaryButton && (
        <ButtonStackButton
          variant="neutral"
          type="outline"
          size="small"
          {...secondaryButton}
          data-testid={
            secondaryButton['data-testid'] || `${testID}-secondary-button`
          }
        />
      )}
      {primaryButton && (
        <ButtonStackButton
          variant="primary"
          type="filled"
          size="small"
          {...primaryButton}
          data-testid={
            primaryButton['data-testid'] || `${testID}-primary-button`
          }
        />
      )}
    </div>
  );
};

ButtonStack.displayName = 'ButtonStack';
